import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  Building,
  CheckCircle2,
  Mail,
  Phone,
  ShieldCheck,
  ShoppingCart,
  Tag,
  Trash2,
  User,
  XCircle,
} from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  clearCarts,
  getCarts,
  getOffers,
  syncCartItems,
} from "../../services/productService";
import { getLocalUserId } from "../../utils/getLocalUserId";
import { getLocalStorage, setLocalStorage } from "../../utils/localStorage";
import { getToken } from "../../utils/getToken";
import { clearStoredUserSession, saveAuthenticatedProfile } from "../../utils/authStorage";
import "./Checkout.css";
import AvailableOn from "../AvailableOn/AvailableOn";
import { buildCartEventPayload, getMetaCookies, META_PIXEL_EVENTS, trackMetaEvent } from "../../utils/metaPixel";
import { getUtmPayload } from "../../utils/utmTracker";

const API_BASE_URL = process.env.REACT_APP_API_URL;
const RAZORPAY_KEY_ID =
  process.env.REACT_APP_RAZORPAY_KEY_ID || "rzp_test_T0Pb1Mk21Nsii0";
const RAZORPAY_CHECKOUT_URL = "https://checkout.razorpay.com/v1/checkout.js";
const RAZORPAY_MODE = RAZORPAY_KEY_ID.startsWith("rzp_test_") ? "test" : "live";
const FREE_SHIPPING_MINIMUM = 699;
const SHIPPING_CHARGE = 99;
const COD_ENABLED = true;
const COD_FEE = 49;

const EMPTY_CHECKOUT_INPUT = {
  name: "",
  phone: "",
  email: "",
  gstNo: "",
  address: "",
  city: "",
  zipcode: "",
  state: "",
};

const getStoredValue = (key) => {
  if (typeof window === "undefined") {
    return "";
  }
  return window.localStorage.getItem(key) || "";
};

const formatCheckoutPrice = (price, { prefix = false, negative = false } = {}) => {
  const amount = Math.round(Number(price) || 0);
  return `${negative ? "-" : ""}${prefix ? "Rs." : ""}${amount}/-`;
};

const getNumericOfferValue = (value) => {
  const numericValue = Number.parseFloat(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const getOfferDiscount = (offer, subtotal) => {
  if (!offer) {
    return 0;
  }
  if (offer.offer_type === "percentage") {
    const percentage = getNumericOfferValue(offer.offer_percentage);
    return Math.round((subtotal * percentage) / 100);
  }
  return getNumericOfferValue(offer.offer_amount);
};

const getOfferTitle = (offer) => {
  if (offer?.offer_type === "percentage") {
    return `Save ${getNumericOfferValue(offer.offer_percentage)}% with`;
  }
  return `Save Rs.${Math.round(getNumericOfferValue(offer?.offer_amount))}/- with`;
};

const getInitialCheckoutInput = () => {
  const savedInput = getLocalStorage("checkoutInput", EMPTY_CHECKOUT_INPUT);

  return {
    ...EMPTY_CHECKOUT_INPUT,
    ...savedInput,
    name:
      savedInput?.name ||
      getStoredValue("auth_name") ||
      getStoredValue("profile_full_name"),
    phone:
      savedInput?.phone ||
      getStoredValue("auth_mobile") ||
      getStoredValue("profile_mobile"),
    email: savedInput?.email || getStoredValue("profile_email"),
    gstNo: savedInput?.gstNo || getStoredValue("profile_gst_no"),
    address: savedInput?.address || getStoredValue("profile_address"),
    city: savedInput?.city || getStoredValue("profile_city"),
    zipcode: savedInput?.zipcode || getStoredValue("profile_pincode"),
    state: savedInput?.state || getStoredValue("profile_state"),
  };
};

const extractApiMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data?.message === "string") {
    return data.message;
  }
  if (typeof data?.errors === "string") {
    return data.errors;
  }
  if (data?.errors && typeof data.errors === "object") {
    const firstError = Object.values(data.errors).flat().find(Boolean);
    if (firstError) {
      return firstError;
    }
  }
  return fallback;
};

const getProfile = (response) => response?.data?.data || {};

const getAuthenticatedUserDetails = (authToken) =>
  axios.get(`${API_BASE_URL}/auth/get-user-details`, {
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${authToken}`,
    },
  });

const getProfileUserId = (profile) =>
  profile?.id || profile?.user_id || profile?.customer_id || null;

const getStoredCartItems = () => {
  try {
    return Object.values(JSON.parse(localStorage.getItem("carts") || "{}"));
  } catch (error) {
    return [];
  }
};

const keyCartItems = (items = []) =>
  items.reduce((acc, item) => {
    if (item?.product_id) {
      acc[String(item.product_id)] = item;
    }
    return acc;
  }, {});

const saveStoredCartItems = (items = []) => {
  localStorage.setItem("carts", JSON.stringify(keyCartItems(items)));
};

const loadRazorpayCheckout = () =>
  new Promise((resolve) => {
    if (typeof window === "undefined") {
      resolve(false);
      return;
    }

    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const existingScript = document.querySelector(
      `script[src="${RAZORPAY_CHECKOUT_URL}"]`
    );

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(true), { once: true });
      existingScript.addEventListener("error", () => resolve(false), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = RAZORPAY_CHECKOUT_URL;
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

function Checkout() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [token, setToken] = useState(getToken());
  const [userId, setUserId] = useState(() => (getToken() ? "" : getLocalUserId()));
  const [guestCartItems, setGuestCartItems] = useState(getStoredCartItems);
  const [checkoutInput, setCheckoutInput] = useState(getInitialCheckoutInput);
  const [errors, setErrors] = useState({});
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(Boolean(getToken()));
  const [otpInvalid, setOtpInvalid] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [orderLoading, setOrderLoading] = useState(false);
  const [paymentMode, setPaymentMode] = useState("Online");
  const isUserAuthenticated = Boolean(token && otpVerified);
  const [activeStep, setActiveStep] = useState(() => (getToken() ? 2 : 1));
  const [couponInput, setCouponInput] = useState("");
  const [appliedOffer, setAppliedOffer] = useState(null);
  const [couponSlideIndex, setCouponSlideIndex] = useState(0);
  const [couponDragOffset, setCouponDragOffset] = useState(0);
  const [isCouponDragging, setIsCouponDragging] = useState(false);
  const couponDragRef = useRef({
    active: false,
    hasMoved: false,
    startX: 0,
    latestX: 0,
  });

  const { data: cartData } = useQuery({
    queryKey: ["get-cart", { user_id: userId }],
    queryFn: getCarts,
    enabled: Boolean(token && userId),
  });

  const { data: userProfile } = useQuery({
    queryKey: ["auth-user-details", token],
    queryFn: () => getAuthenticatedUserDetails(token),
    enabled: Boolean(token),
    retry: false,
    onSuccess: (response) => {
      const profile = getProfile(response);
      const nextUserId = getProfileUserId(profile);

      if (!nextUserId) {
        return;
      }

      localStorage.setItem("user_id", String(nextUserId));
      setUserId(String(nextUserId));
      queryClient.invalidateQueries({ queryKey: ["get-cart"] });
    },
  });

  const { data: offers = [] } = useQuery({
    queryKey: ["checkout-offers"],
    queryFn: getOffers,
    retry: false,
  });

  const apiCartItems = Object.values(cartData?.data?.data || {});
  const carts = token ? apiCartItems : guestCartItems;
  const totalQuantity = carts.reduce(
    (total, item) => total + Number(item.quantity || 0),
    0
  );
  const subtotal = carts.reduce(
    (total, item) => total + Number(item.price || 0) * Number(item.quantity || 0),
    0
  );
  const shipping = subtotal > 0 && subtotal <= FREE_SHIPPING_MINIMUM ? SHIPPING_CHARGE : 0;
  const appliedOfferCartValue = getNumericOfferValue(appliedOffer?.cart_value);
  const isAppliedOfferValid =
    appliedOffer && subtotal >= appliedOfferCartValue;
  const discount = isAppliedOfferValid
    ? Math.min(getOfferDiscount(appliedOffer, subtotal), subtotal + shipping)
    : 0;
  const codFeeAmount = COD_ENABLED && paymentMode === "cod" ? COD_FEE : 0;
  const finalAmount = Math.max(0, subtotal + shipping - discount + codFeeAmount);
  const couponCount = Math.max(offers.length, 1);
  const couponTranslate = couponSlideIndex * (100 / couponCount);

  useEffect(() => {
    window.scrollTo(0, 0);
    // Standard Meta Pixel InitiateCheckout event on checkout page load
    if (carts.length > 0) {
      trackMetaEvent(
        META_PIXEL_EVENTS.INITIATE_CHECKOUT,
        buildCartEventPayload(carts)
      );
    }
  }, []);

  useEffect(() => {
    setCouponSlideIndex(0);

    if (offers.length <= 1) {
      return undefined;
    }

    const slideTimer = window.setInterval(() => {
      if (couponDragRef.current.active) {
        return;
      }

      setCouponSlideIndex((currentIndex) =>
        currentIndex >= offers.length - 1 ? 0 : currentIndex + 1
      );
    }, 3000);

    return () => window.clearInterval(slideTimer);
  }, [offers.length]);

  const handleCouponPointerDown = (event) => {
    if (
      offers.length <= 1 ||
      event.target.closest("button") ||
      (event.pointerType === "mouse" && event.button !== 0)
    ) {
      return;
    }

    couponDragRef.current = {
      active: true,
      hasMoved: false,
      startX: event.clientX,
      latestX: event.clientX,
    };
    setIsCouponDragging(true);
    setCouponDragOffset(0);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleCouponPointerMove = (event) => {
    if (!couponDragRef.current.active) {
      return;
    }

    const offset = event.clientX - couponDragRef.current.startX;
    couponDragRef.current.latestX = event.clientX;
    couponDragRef.current.hasMoved = couponDragRef.current.hasMoved || Math.abs(offset) > 4;
    setCouponDragOffset(offset);
  };

  const finishCouponDrag = (event) => {
    if (!couponDragRef.current.active) {
      return;
    }

    const offset = couponDragRef.current.latestX - couponDragRef.current.startX;
    const slideWidth = event.currentTarget.offsetWidth || 1;
    const swipeThreshold = Math.max(40, slideWidth * 0.18);

    if (Math.abs(offset) >= swipeThreshold) {
      setCouponSlideIndex((currentIndex) => {
        if (offset < 0) {
          return currentIndex >= offers.length - 1 ? 0 : currentIndex + 1;
        }

        return currentIndex <= 0 ? offers.length - 1 : currentIndex - 1;
      });
    }

    couponDragRef.current.active = false;
    setCouponDragOffset(0);
    setIsCouponDragging(false);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  useEffect(() => {
    if (token) {
      return undefined;
    }

    const refreshGuestCart = () => {
      setGuestCartItems(getStoredCartItems());
    };

    refreshGuestCart();
    window.addEventListener("storage", refreshGuestCart);
    window.addEventListener("focus", refreshGuestCart);

    return () => {
      window.removeEventListener("storage", refreshGuestCart);
      window.removeEventListener("focus", refreshGuestCart);
    };
  }, [token]);

  useEffect(() => {
    const profile = getProfile(userProfile);

    if (!profile || Object.keys(profile).length === 0) {
      return;
    }

    const nextUserId = getProfileUserId(profile);
    if (nextUserId) {
      localStorage.setItem("user_id", String(nextUserId));
      setUserId(String(nextUserId));
    }

    saveAuthenticatedProfile(profile, { userId: nextUserId });

    setCheckoutInput((prev) => ({
      ...prev,
      name: prev.name || profile?.first_name || profile?.name || "",
      phone: prev.phone || profile?.phone_no || profile?.mobile || "",
      email: prev.email || profile?.email || "",
      gstNo: prev.gstNo || profile?.gst_no || profile?.gst_number || "",
      address: prev.address || profile?.address || "",
      city: prev.city || profile?.city || "",
      zipcode: prev.zipcode || profile?.pincode || "",
      state: prev.state || profile?.state || "",
    }));
  }, [userProfile]);

  const handleInput = (event) => {
    const { name, value } = event.target;
    setCheckoutInput((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: "" }));
  };

  const buildGenerateOtpFormData = (mobile) => {
    const formData = new FormData();
    formData.append("name", checkoutInput.name.trim());
    formData.append("mobile", mobile);
    return formData;
  };

  const buildVerifyOtpFormData = (mobile) => {
    const formData = new FormData();
    formData.append("mobile", mobile);
    formData.append("otp", otp);
    return formData;
  };

  const buildUserDetailsFormData = () => {
    const formData = new FormData();
    formData.append("gstno", checkoutInput.gstNo.trim());
    formData.append("email", checkoutInput.email.trim());
    formData.append("address", checkoutInput.address.trim());
    formData.append("city", checkoutInput.city.trim());
    formData.append("pincode", checkoutInput.zipcode.trim());
    formData.append("state", checkoutInput.state.trim());
    return formData;
  };

  const handleGetOtp = async () => {
    const mobile = checkoutInput.phone.replace(/\D/g, "");

    if (!checkoutInput.name.trim()) {
      toast.info("Please enter your full name first");
      setErrors((prev) => ({ ...prev, name: "Name is required" }));
      return;
    }

    if (mobile.length !== 10) {
      toast.info("Please enter a valid 10 digit mobile number");
      setErrors((prev) => ({ ...prev, phone: "10-digit number required" }));
      return;
    }

    try {
      setAuthLoading(true);
      const { data } = await axios.post(
        `${API_BASE_URL}/auth/generate-otp`,
        buildGenerateOtpFormData(mobile),
        { headers: { Accept: "application/json" } }
      );

      if (data?.status === false) {
        toast.error(data?.message || "Failed sending OTP");
        return;
      }

      localStorage.setItem("auth_mobile", mobile);
      localStorage.setItem("auth_name", checkoutInput.name.trim());
      setOtpSent(true);
      setOtpInvalid(false);
      toast.success(data?.message || "OTP sent successfully to your mobile");
    } catch (error) {
      toast.error(extractApiMessage(error, "Failed sending OTP"));
    } finally {
      setAuthLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    const mobile = checkoutInput.phone.replace(/\D/g, "");

    if (otp.length !== 4) {
      toast.info("Please enter the 4 digit OTP received");
      return;
    }

    try {
      setAuthLoading(true);
      const { data } = await axios.post(
        `${API_BASE_URL}/auth/verify-otp`,
        buildVerifyOtpFormData(mobile),
        { headers: { Accept: "application/json" } }
      );

      if (data?.status === false) {
        setOtpInvalid(true);
        toast.error(data?.message || "OTP verification failed");
        return;
      }

      const nextToken = data?.token;
      const storedCartItems = getStoredCartItems();

      if (!nextToken) {
        setOtpInvalid(true);
        toast.error("OTP verified, but login token was missing");
        return;
      }

      clearStoredUserSession({ preserveCart: true });
      localStorage.setItem("token", nextToken);
      localStorage.setItem("auth_mobile", mobile);
      localStorage.setItem("auth_name", checkoutInput.name.trim());

      const profileResponse = await getAuthenticatedUserDetails(nextToken);
      const profile = getProfile(profileResponse);
      const nextUserId = getProfileUserId(profile);
      saveAuthenticatedProfile(profile, {
        name: checkoutInput.name.trim(),
        mobile,
        userId: nextUserId,
      });

      if (!nextUserId) {
        setOtpInvalid(true);
        toast.error("OTP verified, but user id was missing");
        return;
      }

      localStorage.setItem("user_id", String(nextUserId));
      setUserId(String(nextUserId));

      if (storedCartItems.length > 0) {
        try {
          await syncCartItems({ user_id: nextUserId, items: storedCartItems });
          queryClient.setQueriesData({ queryKey: ["get-cart"] }, (oldData) => ({
            ...(oldData || {}),
            data: {
              ...(oldData?.data || {}),
              data: keyCartItems(storedCartItems),
            },
          }));
          queryClient.invalidateQueries({ queryKey: ["get-cart"] });
        } catch (error) {
          console.error("Failed syncing guest cart after OTP login", error);
        }
      }

      setToken(nextToken);
      setOtpVerified(true);
      setOtpInvalid(false);
      setActiveStep(2);
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success("Login successful! Please enter your delivery address below.");
    } catch (error) {
      setOtpInvalid(true);
      toast.error(extractApiMessage(error, "OTP verification failed"));
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    clearStoredUserSession({ preserveCart: true });
    setToken(null);
    setOtpVerified(false);
    setOtpSent(false);
    setOtp("");
    setUserId(getLocalUserId());
    setActiveStep(1);
    toast.info("Logged out. Please verify your number to continue.");
  };

  const validateCheckout = () => {
    const requiredFields = ["name", "phone", "email", "address", "city", "zipcode", "state"];
    const nextErrors = {};

    requiredFields.forEach((field) => {
      if (!String(checkoutInput[field] || "").trim()) {
        nextErrors[field] = "Required";
      }
    });

    if (checkoutInput.phone.replace(/\D/g, "").length !== 10) {
      nextErrors.phone = "Enter valid 10-digit mobile";
    }

    if (checkoutInput.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(checkoutInput.email.trim())) {
      nextErrors.email = "Enter a valid email address";
    }

    if (checkoutInput.zipcode && checkoutInput.zipcode.replace(/\D/g, "").length !== 6) {
      nextErrors.zipcode = "Enter valid 6-digit pincode";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const showOfferCartValueToast = (offer) => {
    const cartValue = getNumericOfferValue(offer?.cart_value);
    const remainingAmount = Math.max(0, cartValue - subtotal);

    toast.info(
      `Buy more worth ${formatCheckoutPrice(remainingAmount, { prefix: true })} to apply this offer`
    );
  };

  const findOfferByCode = (code) =>
    offers.find(
      (offer) =>
        String(offer?.coupon_code || "").trim().toUpperCase() ===
        code.trim().toUpperCase()
    );

  const handleApplyCoupon = (event, offerOrCode = couponInput) => {
    event?.preventDefault();
    const selectedOffer =
      typeof offerOrCode === "object"
        ? offerOrCode
        : findOfferByCode(String(offerOrCode || "").replace("%", ""));

    if (!selectedOffer && !String(offerOrCode || "").trim()) {
      toast.info("Please enter coupon code");
      return;
    }

    if (!selectedOffer) {
      toast.error("Invalid coupon");
      return;
    }

    if (subtotal < getNumericOfferValue(selectedOffer.cart_value)) {
      showOfferCartValueToast(selectedOffer);
      return;
    }

    setCouponInput(selectedOffer.coupon_code);
    setAppliedOffer(selectedOffer);
    toast.success("Coupon applied");
  };

  const persistCartItems = async (nextItems) => {
    const cleanItems = nextItems.filter(
      (item) => item?.product_id && Number(item.quantity) > 0
    );

    saveStoredCartItems(cleanItems);
    setGuestCartItems(cleanItems);
    queryClient.setQueriesData({ queryKey: ["get-cart"] }, (oldData) => ({
      ...(oldData || {}),
      data: {
        ...(oldData?.data || {}),
        data: keyCartItems(cleanItems),
      },
    }));

    if (token && userId) {
      if (cleanItems.length === 0) {
        await clearCarts({ user_id: userId });
      } else {
        await syncCartItems({ user_id: userId, items: cleanItems });
      }
      queryClient.invalidateQueries({ queryKey: ["get-cart"] });
    }
  };

  const handleCartQuantity = async (item, quantity) => {
    try {
      const nextItems = carts
        .map((cartItem) =>
          String(cartItem.product_id) === String(item.product_id)
            ? { ...cartItem, quantity }
            : cartItem
        )
        .filter((cartItem) => Number(cartItem.quantity) > 0);

      await persistCartItems(nextItems);
    } catch (error) {
      toast.error("Failed updating cart");
      queryClient.invalidateQueries({ queryKey: ["get-cart"] });
    }
  };

  const handleCartRemove = async (item) => {
    try {
      const nextItems = carts.filter(
        (cartItem) => String(cartItem.product_id) !== String(item.product_id)
      );

      await persistCartItems(nextItems);
    } catch (error) {
      toast.error("Failed removing item");
      queryClient.invalidateQueries({ queryKey: ["get-cart"] });
    }
  };

  const persistCheckoutDetails = () => {
    setLocalStorage("checkoutInput", checkoutInput);
    localStorage.setItem("auth_name", checkoutInput.name);
    localStorage.setItem("auth_mobile", checkoutInput.phone.replace(/\D/g, ""));
    localStorage.setItem("profile_full_name", checkoutInput.name);
    localStorage.setItem("profile_mobile", checkoutInput.phone.replace(/\D/g, ""));
    localStorage.setItem("profile_email", checkoutInput.email);
    localStorage.setItem("profile_gst_no", checkoutInput.gstNo);
    localStorage.setItem("profile_address", checkoutInput.address);
    localStorage.setItem("profile_city", checkoutInput.city);
    localStorage.setItem("profile_pincode", checkoutInput.zipcode);
    localStorage.setItem("profile_state", checkoutInput.state);
  };

  const createCheckoutOrder = async (selectedPaymentMode = "Online") => {
    const profileResponse = await getAuthenticatedUserDetails(token);
    const profile = getProfile(profileResponse);
    const resolvedUserId = getProfileUserId(profile);

    if (!resolvedUserId) {
      toast.error("User ID missing. Please login again.");
      return null;
    }

    localStorage.setItem("user_id", String(resolvedUserId));
    setUserId(String(resolvedUserId));

    // Save/update user address details in backend
    try {
      await axios.post(
        `${API_BASE_URL}/auth/add-user-details`,
        buildUserDetailsFormData(),
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            token,
          },
        }
      );
    } catch (err) {
      console.warn("Could not sync user address details before order", err?.message);
    }

    const orderData = {
      user_id: resolvedUserId,
      first_name: checkoutInput.name.trim(),
      fist_name: checkoutInput.name.trim(),
      email: checkoutInput.email.trim(),
      phone_no: checkoutInput.phone.replace(/\D/g, ""),
      address: checkoutInput.address.trim(),
      city: checkoutInput.city.trim(),
      state: checkoutInput.state.trim(),
      pincode: checkoutInput.zipcode.trim(),
      gst_no: checkoutInput.gstNo.trim(),
      shipment_charge: Number(shipping),
      total_amount: Number(finalAmount),
      payment_mode: selectedPaymentMode,
      payment_status: "Pending",
      razorpay_mode: RAZORPAY_MODE,
      razorpay_key_id: RAZORPAY_KEY_ID,
      shipment_id: "",
      offer_id: isAppliedOfferValid ? appliedOffer.id : null,
      items: carts.map((item) => ({
        product_id: item.product_id,
        quantity: Number(item.quantity),
        price: Number(item.price),
      })),
      meta_tracking: getMetaCookies(),
      ...getUtmPayload(),
    };

    const orderResponse = await axios.post(`${API_BASE_URL}/order/create`, orderData, {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const createdOrder = orderResponse.data?.data || {};

    return {
      order: createdOrder,
      razorpayOrderId: createdOrder?.razorpay_order_id || "",
      userId: resolvedUserId,
    };
  };

  const verifyRazorpayPayment = async (paymentResponse) => {
    const verifyPayload = {
      razorpay_order_id: paymentResponse?.razorpay_order_id || "",
      razorpay_payment_id: paymentResponse?.razorpay_payment_id || "",
      razorpay_signature: paymentResponse?.razorpay_signature || "",
      meta_tracking: getMetaCookies(),
    };

    if (
      !verifyPayload.razorpay_order_id ||
      !verifyPayload.razorpay_payment_id ||
      !verifyPayload.razorpay_signature
    ) {
      throw new Error("Payment credentials missing from Razorpay");
    }

    const verifyResponse = await axios.post(
      `${API_BASE_URL}/order/verify-payment`,
      verifyPayload,
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (verifyResponse.data?.status === false || verifyResponse.data?.result === 0) {
      throw new Error(verifyResponse.data?.message || verifyResponse.data?.msg || "Payment verification failed");
    }

    return verifyResponse.data;
  };

  const openRazorpayPayment = async () => {
    if (!RAZORPAY_KEY_ID) {
      toast.error("Razorpay key is missing");
      setOrderLoading(false);
      return;
    }

    const scriptLoaded = await loadRazorpayCheckout();

    if (!scriptLoaded) {
      toast.error("Failed loading Razorpay checkout popup. Please check your connection.");
      setOrderLoading(false);
      return;
    }

    const createdOrder = await createCheckoutOrder("Online");

    if (!createdOrder) {
      setOrderLoading(false);
      return;
    }

    if (!createdOrder.razorpayOrderId) {
      console.error("Order create response missing razorpay_order_id", createdOrder.order);
      toast.error("Order created, but Razorpay order id was missing");
      setOrderLoading(false);
      return;
    }

    const amountInPaise = Math.round(finalAmount * 100);

    // Standard Razorpay Modal options
    const options = {
      key: RAZORPAY_KEY_ID,
      mode: RAZORPAY_MODE,
      order_id: createdOrder.razorpayOrderId,
      amount: amountInPaise,
      currency: "INR",
      name: "Kuku Namkeen",
      description: "Order Payment",
      image: "/KUKU_LOGO-removebg.png",
      prefill: {
        name: checkoutInput.name.trim(),
        email: checkoutInput.email.trim(),
        contact: checkoutInput.phone.replace(/\D/g, ""),
      },
      notes: {
        order_id: createdOrder.order?.order_id || "",
        address: checkoutInput.address.trim(),
        city: checkoutInput.city.trim(),
        pincode: checkoutInput.zipcode.trim(),
      },
      theme: {
        color: "#952F8A",
      },
      modal: {
        ondismiss: () => {
          setOrderLoading(false);
          toast.info("Payment cancelled");
        },
      },
      handler: async (paymentResponse) => {
        try {
          // Verification API call
          await verifyRazorpayPayment(paymentResponse);
          const orderId = createdOrder.order?.order_id || createdOrder.order?.id;

          // Standard Meta Pixel & CAPI Purchase Event
          trackMetaEvent(
            META_PIXEL_EVENTS.PURCHASE,
            buildCartEventPayload(carts, {
              value: Number(createdOrder.order?.total_amount || finalAmount),
              order_id: orderId,
              payment_type: "Online",
            }),
            {
              eventId: orderId,
              userData: {
                email: checkoutInput.email.trim(),
                phone_no: checkoutInput.phone.replace(/\D/g, ""),
                first_name: checkoutInput.name.trim(),
                city: checkoutInput.city.trim(),
                pincode: checkoutInput.zipcode.trim(),
              },
            }
          );

          localStorage.removeItem("carts");
          await clearCarts({ user_id: createdOrder.userId });
          await queryClient.invalidateQueries({ queryKey: ["get-cart"] });
          toast.success("Payment verified. Order confirmed!");

          const successId = encodeURIComponent(
            createdOrder.order?.order_id || createdOrder.order?.id || "success"
          );
          navigate(`/payment-success/${successId}`, {
            state: { order: createdOrder.order },
          });
        } catch (error) {
          console.error("Error verifying payment", error);
          toast.error(extractApiMessage(error, error?.message || "Payment verification failed"));
        } finally {
          setOrderLoading(false);
        }
      },
    };

    const razorpay = new window.Razorpay(options);
    razorpay.on("payment.failed", (response) => {
      console.error("Razorpay payment failed", response?.error);
      setOrderLoading(false);
      toast.error(response?.error?.description || "Payment failed");
      navigate("/payment-error");
    });
    razorpay.open();
  };

  const placeCodOrder = async () => {
    const createdOrder = await createCheckoutOrder("cod");

    if (!createdOrder) {
      setOrderLoading(false);
      return;
    }

    const orderId = createdOrder.order?.order_id || createdOrder.order?.id;
    trackMetaEvent(
      META_PIXEL_EVENTS.PURCHASE,
      buildCartEventPayload(carts, {
        value: Number(createdOrder.order?.total_amount || finalAmount),
        order_id: orderId,
        payment_type: "COD",
      }),
      {
        eventId: orderId,
        userData: {
          email: checkoutInput.email.trim(),
          phone_no: checkoutInput.phone.replace(/\D/g, ""),
          first_name: checkoutInput.name.trim(),
          city: checkoutInput.city.trim(),
          pincode: checkoutInput.zipcode.trim(),
        },
      }
    );

    localStorage.removeItem("carts");
    await clearCarts({ user_id: createdOrder.userId });
    await queryClient.invalidateQueries({ queryKey: ["get-cart"] });
    toast.success("Order confirmed successfully!");

    const successId = encodeURIComponent(
      createdOrder.order?.order_id || createdOrder.order?.id || "success"
    );
    navigate(`/payment-success/${successId}`, {
      state: { order: createdOrder.order },
    });
    setOrderLoading(false);
  };

  const handlePlaceOrder = async () => {
    if (carts.length === 0) {
      toast.info("Your cart is empty");
      return;
    }

    if (!token || !otpVerified) {
      toast.info("Please login with your mobile number in Step 1 first");
      setActiveStep(1);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    if (!validateCheckout()) {
      toast.info("Please complete all required shipping details");
      return;
    }

    if (finalAmount <= 0) {
      toast.info("Order amount should be greater than zero");
      return;
    }

    persistCheckoutDetails();

    // Standard Meta Pixel AddPaymentInfo event
    trackMetaEvent(
      META_PIXEL_EVENTS.ADD_PAYMENT_INFO,
      buildCartEventPayload(carts, {
        value: Number(finalAmount),
        payment_type: paymentMode === "cod" ? "Cash on Delivery" : "Online",
      })
    );

    setOrderLoading(true);

    try {
      if (paymentMode === "cod") {
        if (!COD_ENABLED) {
          toast.error("Cash on Delivery is temporarily unavailable. Please choose Online Payment.");
          setPaymentMode("Online");
          setOrderLoading(false);
          return;
        }
        await placeCodOrder();
        return;
      }

      await openRazorpayPayment();
    } catch (error) {
      console.error("Error opening Razorpay checkout", error);
      setOrderLoading(false);
      toast.error(extractApiMessage(error, "Failed starting payment"));
    }
  };

  return (
    <main className="checkout-page">
      <section className="section-header-title navigation-space-mob">
        <div className="checkout-container">
          <h1>CHECKOUT</h1>
        </div>
      </section>
      <img
        src="/Image/border-bottom-title.png"
        className="w-full header-title-image"
        alt=""
      />

      <section className="checkout-container checkout-layout">
        {/* Left Column: Cart Items */}
        <div className="checkout-cart-column">
          <div className="checkout-cart-heading">
            <h5>Your Order(s) in Cart</h5>
          </div>

          {carts.length > 0 ? (
            <div className="checkout-table-wrap">
              <table className="checkout-table">
                <tbody>
                  {carts.map((item) => {
                    const itemPrice = Number(item.price || 0);
                    const itemOriginalPrice = Number(item.originalPrice || item.original_price || 0);
                    const itemQuantity = Number(item.quantity || 0);
                    const linePrice = itemPrice * itemQuantity;
                    const originalLinePrice = itemOriginalPrice * itemQuantity;
                    const showOriginalPrice = itemOriginalPrice > itemPrice;

                    return (
                      <tr key={item.product_id}>
                        <td className="checkout-product-image">
                          <img
                            src={
                              item.thumbnail_image ||
                              "/kuku-assets/products/dry-chapti-kachori/1 chapti kachori.jpg"
                            }
                            alt={item.product_name}
                          />
                        </td>
                        <td>
                          <p className="checkout-product-title">{item.product_name}</p>
                          <label>{item.weight ? `Order of ${item.weight}` : "Kuku Namkeen"}</label>
                        </td>
                        <td>
                          <div className="checkout-count">
                            <button
                              type="button"
                              onClick={() =>
                                handleCartQuantity(item, Number(item.quantity || 1) - 1)
                              }
                              aria-label={`Decrease ${item.product_name} quantity`}
                            >
                              -
                            </button>
                            <span>{item.quantity}</span>
                            <button
                              type="button"
                              onClick={() =>
                                handleCartQuantity(item, Number(item.quantity || 0) + 1)
                              }
                              aria-label={`Increase ${item.product_name} quantity`}
                            >
                              +
                            </button>
                          </div>
                        </td>
                        <td className="checkout-line-price">
                          <div className="checkout-price-group">
                            <span>
                              {formatCheckoutPrice(linePrice, { prefix: true })}
                            </span>
                            {showOriginalPrice && (
                              <span className="checkout-line-price-old">
                                {formatCheckoutPrice(originalLinePrice, { prefix: true })}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="checkout-remove-cell">
                          <button
                            type="button"
                            onClick={() => handleCartRemove(item)}
                            aria-label={`Remove ${item.product_name}`}
                          >
                            <Trash2 size={16} strokeWidth={2} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="checkout-empty">
              <ShoppingCart size={32} />
              <p>Your cart is currently empty.</p>
              <Link to="/categories" className="checkout-continue-link">
                Continue Shopping
              </Link>
            </div>
          )}
        </div>

        {/* Right Column: Redesigned Modern Checkout Flow */}
        <aside className="checkout-sidebar">
          {/* Stepper Progress Bar */}
          <div className="checkout-stepper-container">
            <button
              type="button"
              className={`checkout-stepper-btn ${activeStep === 1 ? "is-active" : ""} ${isUserAuthenticated ? "is-done" : ""}`}
              onClick={() => {
                setActiveStep(1);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            >
              <span className="checkout-stepper-num">
                {isUserAuthenticated ? <CheckCircle2 size={15} /> : "1"}
              </span>
              <span className="checkout-stepper-meta">
                <span className="checkout-stepper-step">STEP 1</span>
                <span className="checkout-stepper-name">Account Login</span>
              </span>
            </button>

            <div className={`checkout-stepper-connector ${activeStep === 2 || isUserAuthenticated ? "is-active" : ""}`} />

            <button
              type="button"
              className={`checkout-stepper-btn ${activeStep === 2 ? "is-active" : ""} ${!isUserAuthenticated ? "is-disabled" : ""}`}
              onClick={() => {
                if (isUserAuthenticated) {
                  setActiveStep(2);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                } else {
                  toast.info("Please login and verify your OTP in Step 1 first");
                }
              }}
            >
              <span className="checkout-stepper-num">2</span>
              <span className="checkout-stepper-meta">
                <span className="checkout-stepper-step">STEP 2</span>
                <span className="checkout-stepper-name">Address &amp; Payment</span>
              </span>
            </button>
          </div>

          {/* Multi-step Sliding Viewport: 1 step visible at a time */}
          <div className="checkout-multistep-viewport">
            {/* ==========================================
                SLIDE 1: Account Login / Verification
               ========================================== */}
            <div className={`checkout-step-slide checkout-step-slide--1 ${activeStep === 1 ? "is-active-slide" : "is-exited-left"}`}>
              <div className="checkout-section checkout-step checkout-step-active">
                <div className="checkout-step-header">
                  <span className="checkout-step-number">
                    {isUserAuthenticated ? <CheckCircle2 size={18} color="#16a34a" /> : "1"}
                  </span>
                  <div className="checkout-step-title-wrap">
                    <div className="checkout-step-title-row">
                      <h6>1. Account Verification</h6>
                      {isUserAuthenticated && (
                        <span className="checkout-verified-badge">
                          <CheckCircle2 size={12} /> Verified
                        </span>
                      )}
                    </div>
                    <p className="checkout-step-desc">
                      {isUserAuthenticated
                        ? "Logged in securely with your mobile number"
                        : "Enter your name and mobile number to receive verification OTP"}
                    </p>
                  </div>
                </div>

                {isUserAuthenticated ? (
                  <div className="checkout-auth-verified-container">
                    <div className="checkout-auth-badge">
                      <div className="checkout-auth-user-info">
                        <div className="checkout-auth-avatar">
                          <User size={18} />
                        </div>
                        <div>
                          <strong className="checkout-auth-name">{checkoutInput.name || "Customer"}</strong>
                          <p className="checkout-auth-phone">
                            <Phone size={12} /> +91 {checkoutInput.phone.replace(/\D/g, "")}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="checkout-auth-change-btn"
                        onClick={handleLogout}
                        title="Switch to another mobile number"
                      >
                        Change Account
                      </button>
                    </div>

                    <button
                      type="button"
                      className="checkout-continue-step-btn"
                      onClick={() => {
                        setActiveStep(2);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                    >
                      Proceed to Delivery &amp; Payment <ArrowRight size={16} />
                    </button>
                  </div>
                ) : (
                  <div className="checkout-auth-form">
                    <div className="checkout-form-group">
                      <label className="checkout-field-label">Full Name *</label>
                      <div className="checkout-input-with-icon">
                        <User size={16} className="checkout-input-icon" />
                        <input
                          type="text"
                          name="name"
                          value={checkoutInput.name}
                          onChange={handleInput}
                          placeholder="Enter your full name"
                          className={errors.name ? "checkout-input-error" : ""}
                        />
                      </div>
                      {errors.name && <span className="checkout-field-error">{errors.name}</span>}
                    </div>

                    <div className="checkout-form-group">
                      <label className="checkout-field-label">Mobile Number *</label>
                      <div className="checkout-phone-input-row">
                        <span className="checkout-prefix-badge">+91</span>
                        <input
                          type="tel"
                          name="phone"
                          maxLength="10"
                          value={checkoutInput.phone}
                          onChange={handleInput}
                          placeholder="10-digit mobile number"
                          className={errors.phone ? "checkout-input-error" : ""}
                        />
                        <button
                          type="button"
                          className="checkout-otp-send-btn"
                          onClick={handleGetOtp}
                          disabled={authLoading}
                        >
                          {authLoading && !otpSent ? "Sending..." : otpSent ? "Resend" : "Get OTP"}
                        </button>
                      </div>
                      {errors.phone && <span className="checkout-field-error">{errors.phone}</span>}
                    </div>

                    {otpSent && (
                      <div className="checkout-otp-wrapper">
                        <label className="checkout-field-label">Enter 4-Digit OTP *</label>
                        <div className="checkout-otp-row">
                          <input
                            type="text"
                            inputMode="numeric"
                            maxLength="4"
                            value={otp}
                            onChange={(event) => setOtp(event.target.value.replace(/\D/g, ""))}
                            placeholder="• • • •"
                            className="checkout-otp-input"
                          />
                          <button
                            type="button"
                            className="checkout-otp-verify-btn"
                            onClick={handleVerifyOtp}
                            disabled={authLoading || otp.length !== 4}
                          >
                            {authLoading ? "Verifying..." : "Verify & Continue"}
                          </button>
                        </div>

                        {otpInvalid && (
                          <div className="checkout-status">
                            <label className="checkout-status-error">
                              <XCircle size={15} /> Invalid or expired OTP. Please try again.
                            </label>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* ==========================================
                SLIDE 2: Delivery Address, Coupons, Payment & Summary
               ========================================== */}
            <div className={`checkout-step-slide checkout-step-slide--2 ${activeStep === 2 ? "is-active-slide" : "is-waiting-right"}`}>
              {/* Back to Step 1 Bar */}
              <div className="checkout-step-nav-bar">
                <button
                  type="button"
                  className="checkout-back-step-btn"
                  onClick={() => {
                    setActiveStep(1);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                >
                  <ArrowLeft size={16} /> Back to Account
                </button>
                <div className="checkout-active-account-chip">
                  <span className="checkout-chip-dot" />
                  <span className="checkout-chip-text">
                    {checkoutInput.name || "Customer"} (+91 {checkoutInput.phone.replace(/\D/g, "")})
                  </span>
                </div>
              </div>

              {/* Delivery Address Card */}
              <div className="checkout-section checkout-step checkout-step-active">
                <div className="checkout-step-header">
                  <span className="checkout-step-number">2</span>
                  <div>
                    <h6>2. Delivery Address</h6>
                    <p className="checkout-step-desc">Enter where your order should be delivered</p>
                  </div>
                </div>

                <div className="checkout-address-form">
                  <div className="checkout-form-grid">
                    {/* Recipient Name & Phone */}
                    <div className="checkout-form-group">
                      <label className="checkout-field-label">Recipient Name *</label>
                      <div className="checkout-input-with-icon">
                        <User size={15} className="checkout-input-icon" />
                        <input
                          type="text"
                          name="name"
                          value={checkoutInput.name}
                          onChange={handleInput}
                          placeholder="Recipient full name"
                          className={errors.name ? "checkout-input-error" : ""}
                        />
                      </div>
                      {errors.name && <span className="checkout-field-error">{errors.name}</span>}
                    </div>

                    <div className="checkout-form-group">
                      <label className="checkout-field-label">Contact Number *</label>
                      <div className="checkout-input-with-icon">
                        <Phone size={15} className="checkout-input-icon" />
                        <input
                          type="tel"
                          name="phone"
                          maxLength="10"
                          value={checkoutInput.phone}
                          onChange={handleInput}
                          placeholder="10-digit number"
                          className={errors.phone ? "checkout-input-error" : ""}
                        />
                      </div>
                      {errors.phone && <span className="checkout-field-error">{errors.phone}</span>}
                    </div>

                    {/* Email */}
                    <div className="checkout-form-group checkout-form-group--full">
                      <label className="checkout-field-label">
                        Email Address *
                      </label>
                      <div className="checkout-input-with-icon">
                        <Mail size={15} className="checkout-input-icon" />
                        <input
                          type="email"
                          name="email"
                          value={checkoutInput.email}
                          onChange={handleInput}
                          placeholder="e.g. name@domain.com"
                          className={errors.email ? "checkout-input-error" : ""}
                        />
                      </div>
                      {errors.email && <span className="checkout-field-error">{errors.email}</span>}
                    </div>

                    {/* Address */}
                    <div className="checkout-form-group checkout-form-group--full">
                      <label className="checkout-field-label">
                        Complete Address (House/Flat No., Building, Street, Area) *
                      </label>
                      <textarea
                        name="address"
                        value={checkoutInput.address}
                        onChange={handleInput}
                        placeholder="e.g. Flat 402, Royal Heights, B/h Shivalik Mall, Satellite Road"
                        className={errors.address ? "checkout-input-error" : ""}
                        rows="2"
                      />
                      {errors.address && <span className="checkout-field-error">{errors.address}</span>}
                    </div>

                    {/* City */}
                    <div className="checkout-form-group">
                      <label className="checkout-field-label">City / Town *</label>
                      <input
                        type="text"
                        name="city"
                        value={checkoutInput.city}
                        onChange={handleInput}
                        placeholder="e.g. Ahmedabad"
                        className={errors.city ? "checkout-input-error" : ""}
                      />
                      {errors.city && <span className="checkout-field-error">{errors.city}</span>}
                    </div>

                    {/* Pincode */}
                    <div className="checkout-form-group">
                      <label className="checkout-field-label">Pincode *</label>
                      <input
                        type="text"
                        name="zipcode"
                        maxLength="6"
                        value={checkoutInput.zipcode}
                        onChange={handleInput}
                        placeholder="6-digit pincode"
                        className={errors.zipcode ? "checkout-input-error" : ""}
                      />
                      {errors.zipcode && <span className="checkout-field-error">{errors.zipcode}</span>}
                    </div>

                    {/* State */}
                    <div className="checkout-form-group">
                      <label className="checkout-field-label">State *</label>
                      <input
                        type="text"
                        name="state"
                        value={checkoutInput.state}
                        onChange={handleInput}
                        placeholder="e.g. Gujarat"
                        className={errors.state ? "checkout-input-error" : ""}
                      />
                      {errors.state && <span className="checkout-field-error">{errors.state}</span>}
                    </div>

                    {/* GST */}
                    <div className="checkout-form-group">
                      <label className="checkout-field-label">
                        GST Number <span className="checkout-label-opt">(Optional)</span>
                      </label>
                      <div className="checkout-input-with-icon">
                        <Building size={15} className="checkout-input-icon" />
                        <input
                          type="text"
                          name="gstNo"
                          value={checkoutInput.gstNo}
                          onChange={handleInput}
                          placeholder="GSTIN if business"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* STEP 3: Coupons & Available Offers */}
              <div className="checkout-section checkout-coupon-card">
                <div className="checkout-card-header">
                  <Tag size={18} className="checkout-header-icon" />
                  <h6>Coupons &amp; Offers</h6>
                </div>

                {appliedOffer ? (
                  <div className="checkout-applied-coupon-box">
                    <div className="checkout-applied-left">
                      <CheckCircle2 size={18} color="#16a34a" />
                      <div>
                        <strong>"{appliedOffer.coupon_code}" Applied!</strong>
                        <p>You saved {formatCheckoutPrice(discount, { prefix: true })} on this order</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="checkout-remove-coupon-btn"
                      onClick={() => {
                        setAppliedOffer(null);
                        setCouponInput("");
                        toast.info("Coupon removed");
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <form className="checkout-coupon-form" onSubmit={handleApplyCoupon}>
                    <div className="checkout-coupon-input-wrap">
                      <Tag size={16} className="checkout-coupon-icon" />
                      <input
                        type="text"
                        value={couponInput}
                        onChange={(event) => setCouponInput(event.target.value)}
                        placeholder="Enter coupon code"
                      />
                    </div>
                    <button type="submit" disabled={!couponInput.trim()}>
                      Apply
                    </button>
                  </form>
                )}

                {/* Offers Carousel / Quick Apply */}
                {offers.length > 0 && (
                  <div className="checkout-offers-wrapper">
                    <span className="checkout-offers-heading">Available Offers:</span>
                    <div
                      className={`checkout-coupon-section${isCouponDragging ? " is-dragging" : ""}`}
                      aria-label="Available coupons"
                      onPointerDown={handleCouponPointerDown}
                      onPointerMove={handleCouponPointerMove}
                      onPointerUp={finishCouponDrag}
                      onPointerCancel={finishCouponDrag}
                      onPointerLeave={finishCouponDrag}
                    >
                      <div
                        className="checkout-coupon-track"
                        data-coupon-count={couponCount}
                        style={{
                          "--coupon-count": couponCount,
                          transform: `translateX(calc(-${couponTranslate}% + ${couponDragOffset}px))`,
                        }}
                      >
                        {offers.map((offer) => {
                          const isThisApplied =
                            appliedOffer &&
                            appliedOffer.coupon_code?.toUpperCase() ===
                            offer.coupon_code?.toUpperCase();

                          return (
                            <div className={`checkout-coupon-container ${isThisApplied ? "checkout-coupon-container--applied" : ""}`} key={offer.id || offer.coupon_code}>
                              <p>
                                {getOfferTitle(offer)} <br />
                                <b>"{offer.coupon_code}"</b>
                              </p>
                              <button
                                type="button"
                                disabled={isThisApplied}
                                onClick={(event) => handleApplyCoupon(event, offer)}
                              >
                                {isThisApplied ? "Applied" : "Apply"}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* STEP 4: Payment Method */}
              <div className="checkout-section checkout-payment-mode">
                <div className="checkout-card-header">
                  <ShieldCheck size={18} className="checkout-header-icon" />
                  <h6>Payment Method</h6>
                </div>

                <div className="checkout-payment-options">
                  {/* Online Payment (Razorpay) - Primary */}
                  <label className={`checkout-payment-card ${paymentMode === "Online" ? "is-selected" : ""}`}>
                    <div className="checkout-payment-radio-wrap">
                      <input
                        type="radio"
                        name="paymentMode"
                        value="Online"
                        checked={paymentMode === "Online"}
                        onChange={() => setPaymentMode("Online")}
                      />
                    </div>
                    <div className="checkout-payment-copy">
                      <div className="checkout-payment-title-row">
                        <strong>Online Payment (Razorpay)</strong>
                        <span className="checkout-fast-badge">Fast &amp; Secure</span>
                      </div>
                      <small>UPI (Google Pay, PhonePe, Paytm), Credit/Debit Cards, Net Banking &amp; Wallets</small>
                    </div>
                  </label>

                  {/* Cash on Delivery (COD) */}
                  <label className={`checkout-payment-card ${paymentMode === "cod" ? "is-selected" : ""}`}>
                    <div className="checkout-payment-radio-wrap">
                      <input
                        type="radio"
                        name="paymentMode"
                        value="cod"
                        checked={paymentMode === "cod"}
                        onChange={() => setPaymentMode("cod")}
                      />
                    </div>
                    <div className="checkout-payment-copy">
                      <div className="checkout-payment-title-row">
                        <strong>Cash on Delivery (COD)</strong>
                        {codFeeAmount > 0 ? (
                          <span className="checkout-cod-fee-badge">+Rs.{codFeeAmount} Handling Fee</span>
                        ) : (
                          <span className="checkout-cod-available-badge">Available</span>
                        )}
                      </div>
                      <small>Pay with cash upon delivery at your doorstep</small>
                    </div>
                  </label>
                </div>
              </div>

              {/* STEP 5: Order Summary & Place Order */}
              <div className="checkout-section checkout-summary-card">
                <h6 className="checkout-summary-title">Order Summary</h6>

                <div className="checkout-summary-rows">
                  <div className="checkout-summary-row">
                    <span>Items Subtotal ({totalQuantity} {totalQuantity === 1 ? "item" : "items"}):</span>
                    <strong>{formatCheckoutPrice(subtotal, { prefix: true })}</strong>
                  </div>

                  <div className="checkout-summary-row">
                    <span>Delivery Charges:</span>
                    <strong>
                      {shipping === 0 ? (
                        <span className="checkout-free-shipping-tag">FREE</span>
                      ) : (
                        formatCheckoutPrice(shipping, { prefix: true })
                      )}
                    </strong>
                  </div>

                  {shipping > 0 && (
                    <div className="checkout-shipping-upsell">
                      <span>🚚 Add {formatCheckoutPrice(FREE_SHIPPING_MINIMUM - subtotal + 1, { prefix: true })} more for <strong>FREE DELIVERY!</strong></span>
                    </div>
                  )}

                  {discount > 0 && (
                    <div className="checkout-summary-row checkout-summary-row--discount">
                      <span>Coupon Discount ({appliedOffer?.coupon_code}):</span>
                      <strong>{formatCheckoutPrice(discount, { prefix: true, negative: true })}</strong>
                    </div>
                  )}

                  {codFeeAmount > 0 && (
                    <div className="checkout-summary-row">
                      <span>COD Handling Fee:</span>
                      <strong>{formatCheckoutPrice(codFeeAmount, { prefix: true })}</strong>
                    </div>
                  )}
                </div>

                <hr className="checkout-summary-divider" />

                <div className="checkout-summary-total">
                  <div>
                    <h6>Total Amount</h6>
                    <small className="checkout-tax-inclusive">Inclusive of all taxes</small>
                  </div>
                  <h5 className="checkout-final-price">{formatCheckoutPrice(finalAmount, { prefix: true })}</h5>
                </div>

                <button
                  type="button"
                  className="checkout-now-btn"
                  onClick={handlePlaceOrder}
                  disabled={orderLoading || carts.length === 0}
                >
                  {orderLoading ? (
                    paymentMode === "cod" ? "PLACING COD ORDER..." : "PROCESSING PAYMENT..."
                  ) : !isUserAuthenticated ? (
                    "LOGIN IN STEP 1 TO CONTINUE"
                  ) : paymentMode === "cod" ? (
                    `CONFIRM CASH ON DELIVERY ORDER (${formatCheckoutPrice(finalAmount, { prefix: true })})`
                  ) : (
                    `PLACE ORDER & PAY ${formatCheckoutPrice(finalAmount, { prefix: true })}`
                  )}
                </button>

                <div className="checkout-trust-section">
                  <span className="checkout-secure-note">
                    <ShieldCheck size={14} color="#16a34a" /> 100% Safe &amp; Encrypted Payment powered by Razorpay
                  </span>
                  <div className="checkout-payment-badges">
                    <img src="/kuku-assets/images/payment-badges.png" alt="Accepted payment methods: UPI, Visa, Mastercard, RuPay" />
                  </div>
                  <div className="checkout-policy-links">
                    <Link to="/terms-conditions" target="_blank">Terms &amp; Conditions</Link>
                    <span>•</span>
                    <Link to="/return-refund-policy" target="_blank">Return &amp; Refund Policy</Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </section>

      <AvailableOn />

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </main>
  );
}

export default Checkout;
